import * as localStorage from "@utility/storage/local";
import * as sessionStorage from "@utility/storage/session";
import { tokenPayload } from "@utility/storage/auth";

/* ----- Whose each chat file kept in this browser is, and which chat it's from - so a chat I'm
   taken out of loses every file of it kept here (not only those of the messages still in a
   snapshot), another login never gets one, and a download finishing after the chat was taken
   from me isn't kept. Stored beside the files themselves (an entry in the same "chat-files"
   cache), so it lasts exactly as long as they do: logout and a company switch delete the cache
   and this with it. A file with no entry (kept before this, or its entry lost) can't be told
   whose it is - it goes the first time the index is read ----- */
const FILES_CACHE = "chat-files";
const FILE_PREFIX = "/chat-files/";
const INDEX_KEY = `${FILE_PREFIX}.owners`;
// Files tracked at most - beyond it the oldest kept goes, file and all
export const MAX_CHAT_FILES = 2000;
// Chats taken from me this session, remembered at most (each only blocks a late download)
const MAX_REVOKED = 500;

// The login's user and company (not the session: logout clears every file anyway)
const chatFileOwner = () => {
  const jwt =
    localStorage.getItem(localStorage.AUTH_TOKEN_KEY) || sessionStorage.getItem(sessionStorage.AUTH_TOKEN_KEY);
  const payload = jwt ? tokenPayload(jwt) : null;
  return payload?.userId && payload?.companyId ? `${payload.userId}:${payload.companyId}` : "";
};

let index = null; // messageId -> { owner, conversationId, storedAt }
let loading = null;
let writing = Promise.resolve();
const revoked = new Map(); // `${owner}|${conversationId}` -> when

const openCache = async () => window.caches?.open(FILES_CACHE);
const idOf = (url) => {
  const path = new URL(url, window.location.origin).pathname;
  return path.startsWith(FILE_PREFIX) ? decodeURIComponent(path.slice(FILE_PREFIX.length)) : null;
};

const load = () => {
  if (loading) return loading;
  loading = (async () => {
    const entries = new Map();
    try {
      const cache = await openCache();
      const stored = await cache?.match(INDEX_KEY);
      if (stored) (await stored.json()).forEach(([id, entry]) => entries.set(id, entry));
      // Files nobody can be named for go
      const keys = (await cache?.keys()) ?? [];
      await Promise.all(
        keys.map((request) => {
          const id = idOf(request.url);
          return id && id !== ".owners" && !entries.has(id) ? cache.delete(request) : null;
        })
      );
    } catch {
      // No browser storage: nothing kept, nothing to track
    }
    index = entries;
    return entries;
  })();
  return loading;
};

// Written one after another, so an older write never lands after a newer one
const save = () => {
  writing = writing.then(async () => {
    // Logged out meanwhile: nothing to write back into the emptied cache
    if (!index) return;
    try {
      const cache = await openCache();
      await cache?.put(INDEX_KEY, new Response(JSON.stringify([...index])));
    } catch {
      // Storage full or unavailable - the files it can't name go next time it's read
    }
  });
  return writing;
};

const deleteFiles = async (ids) => {
  try {
    const cache = await openCache();
    await Promise.all(ids.map((id) => cache?.delete(`${FILE_PREFIX}${id}`)));
  } catch {
    // Nothing stored
  }
};

const revokedKey = (owner, conversationId) => `${owner}|${conversationId}`;
// Taken from me this session (until it's in my chat list again, or I log out)
export const chatFilesRevoked = (conversationId) =>
  !!conversationId && revoked.has(revokedKey(chatFileOwner(), conversationId));

/* ----- A file about to be kept: noted as mine and this chat's. False - so it isn't kept -
   with no login, or for a chat taken from me meanwhile ----- */
export const recordChatFile = async (messageId, conversationId) => {
  const owner = chatFileOwner();
  if (!owner || chatFilesRevoked(conversationId)) return false;
  await load();
  index.set(messageId, { owner, conversationId: conversationId ?? null, storedAt: Date.now() });
  if (index.size > MAX_CHAT_FILES) {
    const oldest = [...index.entries()]
      .sort(([, a], [, b]) => a.storedAt - b.storedAt)
      .slice(0, index.size - MAX_CHAT_FILES)
      .map(([id]) => id);
    oldest.forEach((id) => index.delete(id));
    await deleteFiles(oldest);
  }
  await save();
  return true;
};

// Kept here for this login - another's (or one nobody's named for) is never handed back
export const ownsChatFile = async (messageId) => {
  const owner = chatFileOwner();
  if (!owner) return false;
  await load();
  return index.get(messageId)?.owner === owner;
};

// Taken out of the chat: every file of it kept for this login goes. Resolves their ids
export const revokeChatFiles = async (conversationId) => {
  const owner = chatFileOwner();
  if (!owner || !conversationId) return [];
  if (revoked.size >= MAX_REVOKED) revoked.delete(revoked.keys().next().value);
  revoked.set(revokedKey(owner, conversationId), Date.now());
  await load();
  const ids = [...index.entries()]
    .filter(([, entry]) => entry.owner === owner && entry.conversationId === conversationId)
    .map(([id]) => id);
  if (!ids.length) return [];
  ids.forEach((id) => index.delete(id));
  await deleteFiles(ids);
  await save();
  return ids;
};

// Back in my chat list (let in again): its files can be kept again
export const allowChatFiles = (conversationIds) => {
  const owner = chatFileOwner();
  conversationIds.forEach((conversationId) => revoked.delete(revokedKey(owner, conversationId)));
};

// The chats this login has files kept from
export const conversationsWithFiles = async () => {
  const owner = chatFileOwner();
  if (!owner) return [];
  await load();
  return [
    ...new Set(
      [...index.values()].filter((entry) => entry.owner === owner && entry.conversationId).map((e) => e.conversationId)
    ),
  ];
};

// Removed files (one by one, by message): no longer tracked either
export const dropChatFileEntries = async (messageIds) => {
  await load();
  messageIds.forEach((id) => index.delete(id));
  await save();
};

// Logged out or company switched: the cache (and this index in it) is deleted - nothing kept.
// Resolves once any write on its way has finished, so it can't land after the cache is gone
export const forgetChatFileOwners = () => {
  index = null;
  loading = null;
  revoked.clear();
  return writing;
};
